/**
 * Camada de IA (03/09/2026) — Card "IA (OpenRouter)" da Administração:
 * chave de API (PasswordInput — nunca exibida nem auditada; deixar em branco
 * mantém a atual), combo PESQUISÁVEL de modelos com preço (GET /ia/modelos,
 * catálogo em US$/1M tokens), estimativa de custo por análise, "Testar
 * conexão" (chamada real à OpenRouter) e "Salvar" (PUT só com os campos
 * editados — a chave só viaja se o dono digitou uma nova).
 *
 * 02/09/2026 (pedido do dono) — lista CURADA de modelos: multi-select
 * pesquisável "Modelos disponíveis para o usuário" (modelosHabilitados no
 * PUT). Lista VAZIA = sem restrição (todos do catálogo); o modelo padrão é
 * escolhido ENTRE os habilitados (validação client-side antes de salvar).
 *
 * Nenhum modelo/chave é hardcoded: o default vem do backend.
 */
import { useEffect, useState } from "react";
import { Alert, Anchor, Button, Card, Group, MultiSelect, PasswordInput, Select, Text, Title } from "@mantine/core";
import { IconPlugConnected, IconRobot } from "@tabler/icons-react";
import { api } from "./api";
import { notificarErroApi, notificarOk } from "./componentes/notificacoes";
import {
  TOKENS_PROMPT_ANALISE,
  TOKENS_RESPOSTA_ANALISE,
  custoAnaliseEstimado,
  fmtUsd,
  type ConfigIa,
  type ConfigIaSalva,
  type ModeloIa,
} from "./ia-tipos";

function rotuloModelo(m: ModeloIa): string {
  return `${m.nome} — US$ ${m.precoPromptUsd1M} / US$ ${m.precoRespostaUsd1M} por 1M tokens (prompt/resposta)`;
}

export function ConfigIaCard() {
  const [config, setConfig] = useState<ConfigIa | null>(null);
  const [erroCarga, setErroCarga] = useState<unknown>(null);
  const [chaveDigitada, setChaveDigitada] = useState("");
  const [modelo, setModelo] = useState<string | null>(null);
  // 02/09/2026 — lista curada: [] = todos os modelos do catálogo permitidos
  const [habilitados, setHabilitados] = useState<string[]>([]);
  const [modelos, setModelos] = useState<ModeloIa[] | null>(null);
  const [erroModelos, setErroModelos] = useState(false);
  const [carregandoModelos, setCarregandoModelos] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [testando, setTestando] = useState(false);
  const [teste, setTeste] = useState<{ ok: boolean; detalhe: string } | null>(null);

  async function carregarConfig() {
    try {
      setErroCarga(null);
      const c = await api.get<ConfigIa>("/configuracoes/ia");
      setConfig(c);
      setModelo(c.modelo);
      setHabilitados(c.modelosHabilitados ?? []);
    } catch (e) {
      setErroCarga(e);
    }
  }

  async function carregarModelos() {
    try {
      setErroModelos(false);
      setCarregandoModelos(true);
      const r = await api.get<{ total: number; modelos: ModeloIa[] }>("/ia/modelos");
      setModelos(r.modelos);
    } catch {
      setErroModelos(true);
    } finally {
      setCarregandoModelos(false);
    }
  }

  useEffect(() => {
    void carregarConfig();
    void carregarModelos();
  }, []);

  async function salvar() {
    try {
      // validação client-side (02/09/2026): com a lista curada preenchida, o
      // modelo padrão PRECISA estar entre os habilitados — senão o usuário do
      // passo 7 nunca conseguiria usar o padrão
      if (habilitados.length > 0 && modelo && !habilitados.includes(modelo)) {
        notificarErroApi(
          new Error(
            "O modelo padrão precisa estar entre os modelos disponíveis para o usuário — inclua ele na lista ou escolha outro padrão.",
          ),
        );
        return;
      }
      setSalvando(true);
      setTeste(null);
      // só manda o que o dono EDITOU: chave vazia não viaja (manter a atual);
      // modelo só viaja se mudou; modelosHabilitados só viaja se mudou
      const corpo: { chave?: string; modelo?: string; modelosHabilitados?: string[] } = {};
      if (chaveDigitada.trim()) corpo.chave = chaveDigitada.trim();
      if (modelo && modelo !== config?.modelo) corpo.modelo = modelo;
      const habilitadosMudou =
        config !== null &&
        [...habilitados].sort().join("") !== [...(config.modelosHabilitados ?? [])].sort().join("");
      if (habilitadosMudou) corpo.modelosHabilitados = habilitados;
      if (Object.keys(corpo).length === 0) {
        notificarOk("Nada para salvar — nem a chave, nem o modelo, nem a lista de disponíveis foram alterados.");
        return;
      }
      const r = await api.put<ConfigIaSalva>("/configuracoes/ia", corpo);
      setConfig(r);
      setModelo(r.modelo);
      setHabilitados(r.modelosHabilitados ?? []);
      setChaveDigitada("");
      notificarOk(r.chaveAtualizada ? "Configuração salva — a chave da IA foi atualizada." : "Configuração salva.");
    } catch (e) {
      notificarErroApi(e);
    } finally {
      setSalvando(false);
    }
  }

  async function testar() {
    try {
      setTestando(true);
      setTeste(null);
      const r = await api.post<{ ok: boolean; detalhe: string }>("/configuracoes/ia/testar");
      setTeste(r);
    } catch (e) {
      notificarErroApi(e);
    } finally {
      setTestando(false);
    }
  }

  const modeloSelecionado = modelos?.find((m) => m.id === modelo) ?? null;
  const custo = custoAnaliseEstimado(modeloSelecionado);
  // 02/09/2026 — o padrão é escolhido ENTRE os habilitados (lista vazia = todos)
  const modelosParaPadrao = habilitados.length > 0 ? (modelos ?? []).filter((m) => habilitados.includes(m.id)) : (modelos ?? []);
  // o modelo configurado pode não estar na página da lista — entra como opção extra
  const opcoes = [
    ...modelosParaPadrao.map((m) => ({ value: m.id, label: rotuloModelo(m) })),
    ...(modelo && modelos && !modelosParaPadrao.some((m) => m.id === modelo)
      ? [{ value: modelo, label: `${modelo} (configurado — fora da lista carregada)` }]
      : []),
  ];
  const opcoesHabilitados = (modelos ?? []).map((m) => ({ value: m.id, label: rotuloModelo(m) }));

  return (
    <Card withBorder shadow="xs" mb="md">
      <Group gap="sm" mb="sm">
        <IconRobot size={20} color="var(--mantine-color-sillis-7)" />
        <Title order={4}>IA (OpenRouter)</Title>
      </Group>
      <Text size="sm" mb="sm">
        Liga a análise inteligente das divergências do passo 7 da competência. A IA <strong>propõe</strong> correções,
        mas <strong>nunca aplica nada</strong> — toda sugestão passa pela sua prévia antes de valer.
      </Text>
      {erroCarga !== null && (
        <Alert color="red" title="Não deu para carregar a configuração da IA" mb="sm">
          <Group gap="xs">
            <Text size="sm">Verifique se a API está no ar e tente de novo.</Text>
            <Button size="xs" variant="light" onClick={() => void carregarConfig()}>
              Tentar de novo
            </Button>
          </Group>
        </Alert>
      )}
      {config && !config.chaveConfigurada && (
        <Alert color="yellow" title="Nenhuma chave configurada" mb="sm">
          Crie uma chave em <Anchor href="https://openrouter.ai" target="_blank" rel="noreferrer">openrouter.ai</Anchor> e
          cole aqui. Enquanto não configurar, a análise com IA do passo 7 fica indisponível.
        </Alert>
      )}
      <Group align="flex-end" wrap="wrap" gap="md" mb="sm">
        <PasswordInput
          label="Chave da OpenRouter"
          placeholder={
            config?.chaveConfigurada && config.chaveMascarada
              ? `Configurada: ${config.chaveMascarada} — deixe em branco para manter`
              : "sk-or-…"
          }
          description="A chave nunca é exibida depois de salva nem gravada em auditoria."
          value={chaveDigitada}
          onChange={(e) => setChaveDigitada(e.currentTarget.value)}
          w={380}
          autoComplete="off"
        />
        <Select
          label="Modelo padrão"
          placeholder={carregandoModelos ? "Carregando a lista de modelos…" : "Escolha o modelo"}
          searchable
          data={opcoes}
          value={modelo}
          onChange={setModelo}
          w={380}
          disabled={modelos === null && !erroModelos}
          nothingFoundMessage="Nenhum modelo com esse nome"
          description={
            habilitados.length > 0
              ? "Escolhido entre os modelos disponíveis para o usuário (lista abaixo)."
              : "Sem restrição de modelos — qualquer um do catálogo pode ser o padrão."
          }
        />
      </Group>
      <MultiSelect
        label="Modelos disponíveis para o usuário"
        placeholder={carregandoModelos ? "Carregando a lista de modelos…" : "Escolha os modelos que aparecem no passo 7"}
        searchable
        data={opcoesHabilitados}
        value={habilitados}
        onChange={setHabilitados}
        disabled={modelos === null && !erroModelos}
        nothingFoundMessage="Nenhum modelo com esse nome"
        mb="sm"
        description="Deixe VAZIO para permitir todos os modelos do catálogo. Com a lista preenchida, só estes aparecem no seletor do passo 7 — e o modelo padrão precisa ser um deles."
      />
      {erroModelos && (
        <Alert color="yellow" variant="light" mb="sm" title="Lista de modelos indisponível">
          <Group gap="xs">
            <Text size="sm">Não deu para carregar os preços da OpenRouter — dá para salvar mesmo assim.</Text>
            <Button size="xs" variant="light" onClick={() => void carregarModelos()} loading={carregandoModelos}>
              Tentar de novo
            </Button>
          </Group>
        </Alert>
      )}
      {custo !== null && (
        <Text size="xs" c="dimmed" mb="sm">
          Estimativa: ~{fmtUsd(custo)} por análise de competência (assumindo ~{TOKENS_PROMPT_ANALISE / 1000} mil tokens de
          prompt + ~{TOKENS_RESPOSTA_ANALISE / 1000} mil de resposta).
        </Text>
      )}
      {teste && (
        <Alert color={teste.ok ? "green" : "red"} title={teste.ok ? "Conexão funcionando" : "Falha na conexão"} mb="sm">
          <Text size="sm">{teste.detalhe}</Text>
        </Alert>
      )}
      <Group gap="sm">
        <Button
          variant="light"
          leftSection={<IconPlugConnected size={16} />}
          onClick={() => void testar()}
          loading={testando}
          disabled={config !== null && !config.chaveConfigurada && !chaveDigitada.trim()}
          title={
            config !== null && !config.chaveConfigurada && !chaveDigitada.trim()
              ? "Configure (e salve) uma chave antes de testar"
              : "Faz uma chamada mínima real à OpenRouter com o modelo configurado"
          }
        >
          Testar conexão
        </Button>
        <Button onClick={() => void salvar()} loading={salvando}>
          Salvar
        </Button>
      </Group>
    </Card>
  );
}
